/**
 * Authentication Debug Utilities
 * Use these functions in the browser console to debug auth issues
 */

import { debugAuthStatus, debugAdminAuth, hasValidCookies } from '@/lib/api';
import config from '@/config';

/**
 * Quick debug function to run in browser console
 * Usage: window.debugAuth()
 */
export function debugAuth() {
  console.log('🔍 === AUTHENTICATION DEBUG ===');
  console.log('🌐 API URL:', config.apiUrl);
  
  // 1. Check current auth status
  const authStatus = debugAuthStatus();
  
  // 2. Check if we have valid cookies
  const hasCookies = hasValidCookies();
  console.log('🍪 Has valid cookies:', hasCookies);
  
  // 3. Check session timeout
  const lastActivity = localStorage.getItem('lastActivity');
  const now = Date.now();
  const timeSinceActivity = lastActivity ? now - parseInt(lastActivity) : 0;
  const sessionTimeout = 4 * 60 * 60 * 1000; // 4 hours
  const timeUntilExpiry = sessionTimeout - timeSinceActivity;
  
  // 4. Check token refresh timing (dynamic JWT parsing)
  const tokenRefreshThreshold = 5 * 60 * 1000; // 5 minutes
  
  // Get access token from cookies and parse expiry
  const cookies = document.cookie.split(';');
  const accessTokenCookie = cookies.find(cookie => 
    cookie.trim().toLowerCase().startsWith('adminaccesstoken=')
  );
  
  let timeUntilTokenExpiry = 0;
  let shouldRefreshSoon = false;
  let tokenInfo = 'No access token found';
  
  if (accessTokenCookie) {
    const accessToken = accessTokenCookie.split('=')[1];
    try {
      // Parse JWT payload to get expiry
      const parts = accessToken.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        const expiryTime = payload.exp * 1000; // Convert to milliseconds
        timeUntilTokenExpiry = Math.max(0, expiryTime - now);
        shouldRefreshSoon = timeUntilTokenExpiry <= tokenRefreshThreshold && timeUntilTokenExpiry > 0;
        tokenInfo = `Expires in ${Math.ceil(timeUntilTokenExpiry / 60000)} minutes`;
      }
    } catch (error) {
      tokenInfo = 'Invalid token format';
    }
  }
  
  console.log('⏰ Session timing:', {
    lastActivity: lastActivity ? new Date(parseInt(lastActivity)).toLocaleString() : 'Never',
    timeSinceActivity: Math.round(timeSinceActivity / 1000 / 60) + ' minutes ago',
    timeUntilExpiry: Math.round(timeUntilExpiry / 1000 / 60) + ' minutes remaining',
    isExpired: timeUntilExpiry <= 0
  });
  
  console.log('🎫 Token timing:', {
    tokenInfo,
    timeUntilTokenExpiry: Math.round(timeUntilTokenExpiry / 1000 / 60) + ' minutes remaining',
    shouldRefreshSoon: shouldRefreshSoon,
    tokenRefreshThreshold: Math.round(tokenRefreshThreshold / 1000 / 60) + ' minutes'
  });
  
  // 5. Test API connection
  debugAdminAuth().then(result => {
    console.log('🧪 API test result:', result);
  });
  
  return {
    authStatus,
    hasCookies,
    timeUntilExpiry: Math.round(timeUntilExpiry / 1000 / 60),
    timeUntilTokenExpiry: Math.round(timeUntilTokenExpiry / 1000 / 60),
    shouldRefreshSoon,
    isExpired: timeUntilExpiry <= 0
  };
}

/**
 * Check what cookies are available
 */
export function checkCookies() {
  console.log('🍪 === COOKIE ANALYSIS ===');
  
  if (typeof document === 'undefined') {
    console.log('❌ Not in browser environment');
    return;
  }
  
  const cookies = document.cookie;
  console.log('All cookies:', cookies);
  
  if (!cookies) {
    console.log('❌ No cookies found');
    return;
  }
  
  const cookieList = cookies.split(';').map(c => c.trim());
  console.log('Cookie list:', cookieList);
  
  const authCookies = cookieList.filter(c => 
    c.toLowerCase().includes('adminaccesstoken') ||
    c.toLowerCase().includes('adminrefreshtoken') ||
    c.toLowerCase().includes('admin') || 
    c.toLowerCase().includes('token') || 
    c.toLowerCase().includes('auth')
  );
  
  console.log('Auth-related cookies:', authCookies);
  
  return {
    total: cookieList.length,
    authRelated: authCookies.length,
    cookies: cookieList,
    authCookies
  };
}

/**
 * Force refresh the session
 */
export async function forceRefresh() {
  console.log('🔄 === FORCING SESSION REFRESH ===');
  console.log('Using API URL:', config.apiUrl);
  
  try {
    const response = await fetch(`${config.apiUrl}/api/admin/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    });
    
    console.log('Refresh response:', {
      status: response.status,
      ok: response.ok
    });
    
    if (response.ok) {
      console.log('✅ Refresh successful');
      const data = await response.json();
      console.log('Refresh data:', data);
    } else {
      const error = await response.text();
      console.log('❌ Refresh failed:', error);
    }
    
    return response.ok;
  } catch (error) {
    console.error('❌ Refresh error:', error);
    return false;
  }
}

// Make functions available globally for console debugging
if (typeof window !== 'undefined') {
  (window as any).debugAuth = debugAuth;
  (window as any).checkCookies = checkCookies;
  (window as any).forceRefresh = forceRefresh;
  
  console.log('🔧 Debug functions available:');
  console.log('  - debugAuth() - Full authentication debug');
  console.log('  - checkCookies() - Check available cookies');
  console.log('  - forceRefresh() - Force session refresh');
}
